import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "@/lib/theme";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/layout/Layout";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import Industries from "@/pages/Industries";
import IndustryDetail from "@/pages/IndustryDetail";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import QuoteRequest from "@/pages/QuoteRequest";
import Portfolio from "@/pages/Portfolio";
import PortfolioDetail from "@/pages/PortfolioDetail";
import CaseStudies from "@/pages/CaseStudies";
import CaseStudyDetail from "@/pages/CaseStudyDetail";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import ProjectDetail from "@/pages/ProjectDetail";
import Careers from "@/pages/Careers";
import CareersDetail from "@/pages/CareersDetail";
import Partnership from "@/pages/Partnership";
import LocalePage from "@/pages/LocalePage";
import UKServicePage from "@/pages/UKServicePage";
import GlasgowPage from "@/pages/GlasgowPage";
import EducationIndustryPage from "@/pages/EducationIndustryPage";
import NotFound from "@/pages/NotFound";
import GTM from "@/components/common/GTM";
import { initAttribution } from "@/lib/attribution";
import { resolveRedirect } from "@/lib/redirects";
import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import "@/App.css";

// Client-side legacy redirect hook. Real 301s live at the hosting layer;
// this is a defence-in-depth fallback if a legacy URL slips through.
function LegacyRedirect() {
  const { pathname, search, hash } = useLocation();
  const target = resolveRedirect(pathname);
  const external = !!target && /^https?:\/\//.test(target);
  useEffect(() => { if (external) window.location.replace(target); }, [external, target]);
  if (!target || external) return null;
  return <Navigate to={target + search + hash} replace />;
}

export default function App() {
  useEffect(() => { initAttribution(); }, []);
  return (
    <HelmetProvider>
      <ThemeProvider>
        <BrowserRouter>
          <LegacyRedirect />
          <GTM />
          <Routes>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/products" element={<Products />} />
              <Route path="/products/:slug" element={<ProductDetail />} />
              <Route path="/industries" element={<Industries />} />
              <Route path="/industries/education" element={<EducationIndustryPage />} />
              <Route path="/industries/:slug" element={<IndustryDetail />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/quote" element={<QuoteRequest />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/portfolio/:slug" element={<PortfolioDetail />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
              <Route path="/projects/:slug" element={<ProjectDetail />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/careers/:slug" element={<CareersDetail />} />
              <Route path="/partnership" element={<Partnership />} />
              <Route path="/uk" element={<LocalePage />} />
              <Route path="/uk/:slug" element={<UKServicePage />} />
              <Route path="/locations/glasgow" element={<GlasgowPage />} />
              <Route path="/us" element={<LocalePage />} />
              <Route path="/pk" element={<LocalePage />} />
              <Route path="/de" element={<LocalePage />} />
              <Route path="/sa" element={<LocalePage />} />
              <Route path="/ae" element={<LocalePage />} />
              <Route path="/europe" element={<LocalePage />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors closeButton />
      </ThemeProvider>
    </HelmetProvider>
  );
}
